import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Crown, Package, Award, Play, Check, ChevronRight, Percent, Gift, Bell, Droplet, Sparkles } from "lucide-react";
import { API, PRODUCT_BANNER_IMAGE } from "../constants";
import { useCart } from "../context/CartContext";
import TestimonialsSection from "../components/TestimonialsSection";
import { categorySlug } from "./CategoryLandingPage";

const CATEGORY_TAGLINES = {
  "Metal Polish": "Mirror finish. Every surface.",
  "Compounds": "Erase swirls & oxidation.",
  "Cleaners": "Prep like a pro.",
  "Waxes": "Depth, gloss, armor.",
  "Protectants": "Ceramic-grade protection.",
  "Dressings": "Tire & trim done right.",
  "Interior": "Leather, vinyl, fabric.",
  "Kits": "Everything in one box.",
  "Buffing Wheels": "Cotton, sisal, airway.",
  "Goblets & Tapers": "Reach every corner.",
  "Adapters & Accessories": "Fit any polisher.",
  "Accessories": "Finishing extras.",
};

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categoryBuckets, setCategoryBuckets] = useState([]);
  const [trueBlueProducts, setTrueBlueProducts] = useState([]);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?limit=4`);
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products');
      }
    };
    fetchProducts();

    // Category strip data: bucket every active product by category + pick a sample image.
    axios.get(`${API}/products?limit=500&in_stock=true`).then((res) => {
      const map = {};
      (res.data || []).forEach((p) => {
        const c = p.category || 'Other';
        if (!map[c]) map[c] = { name: c, count: 0, sample: null };
        map[c].count += 1;
        if (!map[c].sample && p.image_url) map[c].sample = p.image_url;
      });
      const order = ["Metal Polish", "Compounds", "Cleaners", "Waxes", "Kits", "Buffing Wheels"];
      const ordered = order.filter((n) => map[n]).map((n) => map[n]);
      const rest = Object.values(map).filter((b) => !order.includes(b.name));
      setCategoryBuckets([...ordered, ...rest].slice(0, 8));
    }).catch(() => {});

    // True Blue spotlight
    axios.get(`${API}/products?limit=6&brand=True Blue&in_stock=true`).then((res) => {
      setTrueBlueProducts(res.data || []);
    }).catch(() => {});

    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={PRODUCT_BANNER_IMAGE} alt="Metal Polishing" className="w-full h-full object-cover opacity-60" />
          <div className="hero-gradient absolute inset-0"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_3ae66389-b093-4330-9743-6c6a00dc5113/artifacts/2iyfs3ge_ChatGPT%20Image%20Dec%2022%2C%202025%2C%2012_56_32%20PM%20-%20Copy.png"
            alt="Cut Above Polish Shop Logo"
            className="h-48 sm:h-64 mx-auto mb-8 animate-fade-in-up"
          />
          <p className="text-xl sm:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto animate-fade-in-up stagger-2">
            Products by the people who know what works
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up stagger-3">
            <Link to="/products" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-8 py-4 rounded-sm font-bold uppercase tracking-wider transition-all btn-glow" data-testid="hero-shop-btn">
              Shop Now
            </Link>
            <Link to="/vip" className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-4 rounded-sm font-bold uppercase tracking-wider transition-all" data-testid="hero-vip-btn">
              Join VIP - $3.99/mo
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { id: 'premium-products', icon: <Package className="w-8 h-8" />, title: 'Premium Products', desc: 'Industry-leading polishing supplies' },
              { id: 'experience', icon: <Award className="w-8 h-8" />, title: '35+ Years Experience', desc: 'Learn from Jimmy Dandridge' },
              { id: 'vip-perks', icon: <Crown className="w-8 h-8" />, title: 'VIP Perks', desc: 'Exclusive discounts & gifts' },
              { id: 'video-training', icon: <Play className="w-8 h-8" />, title: 'Video Training', desc: 'Beginner to advanced courses' },
            ].map((feature) => (
              <div key={feature.id} className="text-center p-6 bg-gradient-to-b from-[#1A1A1A] to-[#0F0F0F] border border-white/5 rounded-sm hover:border-white/20 transition-all">
                <div className="text-[#D62828] mb-4 flex justify-center">{feature.icon}</div>
                <h3 className="font-heading text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-16 bg-[#0A0A0A] border-t border-white/5" data-testid="about-us">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-wide mb-4">ABOUT CUT ABOVE POLISH SHOP</h2>
          <p className="text-gray-300 mb-4">Family owned and operated for 35 years in the same area, dedicated to helping drivers, detailers, and show truck owners get a mirror finish every time.</p>
          <p className="text-gray-400 mb-4">We live and breathe polishing. From first-time beginners to seasoned pros, our team is here to answer questions, recommend the right products, and make sure you have everything you need to shine.</p>
          <p className="text-gray-200 font-semibold mb-6">Our mission is simple: give everyday drivers and professional polishers the same proven tools and techniques we use in our own shop. Products by the people who know what works.</p>
          <a href="https://www.cutabovepolishshop.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#D62828] hover:bg-[#B91C1C] text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wider transition-all">
            Visit Main Shop Website<ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Shop by Category Strip */}
      {categoryBuckets.length > 0 && (
        <section className="py-16 bg-[#050505] border-t border-white/5" data-testid="home-category-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Shop By Category
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-wide">Find What You Need Faster</h2>
              </div>
              <Link to="/categories" className="text-[#D4AF37] hover:text-white text-sm uppercase tracking-widest font-semibold transition-colors flex items-center gap-2" data-testid="home-categories-see-all">
                All Categories <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categoryBuckets.map((c, idx) => (
                <Link
                  key={c.name}
                  to={`/category/${categorySlug(c.name)}`}
                  className="group relative overflow-hidden bg-[#0F0F0F] border border-white/5 hover:border-[#D4AF37]/50 rounded-sm transition-all duration-300 hover:-translate-y-1"
                  data-testid={`home-category-tile-${idx}`}
                >
                  <div className="relative h-32 bg-[#1A1A1A] overflow-hidden">
                    {c.sample && (
                      <img src={c.sample} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/40 to-transparent" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-heading text-base sm:text-lg text-white group-hover:text-[#D4AF37] transition-colors truncate">{c.name}</h3>
                      <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                    <p className="text-gray-500 text-xs mb-1 truncate">{CATEGORY_TAGLINES[c.name] || "Explore the collection."}</p>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500"><span className="text-white font-bold">{c.count}</span> products</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* True Blue Spotlight */}
      {trueBlueProducts.length > 0 && (
        <section
          className="py-20 bg-gradient-to-r from-[#0a1a3a] via-[#0F1A2F] to-[#0a1a3a] relative overflow-hidden border-t border-blue-500/10"
          data-testid="true-blue-spotlight"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <p className="text-blue-300 text-xs font-bold tracking-[0.3em] uppercase mb-2 flex items-center gap-2">
                  <Droplet className="w-4 h-4" /> Vendor Spotlight
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-wide text-white">TRUE BLUE INDUSTRIES</h2>
                <p className="text-blue-100/80 text-sm sm:text-base max-w-2xl mt-2">Cult-favorite polish from 2 Tone's — pro-grade formulas trusted by show truck owners and detailers who demand mirror finishes.</p>
              </div>
              <Link
                to="/products?brand=True Blue"
                className="inline-flex items-center gap-2 bg-white text-[#0a1a3a] hover:bg-blue-200 px-5 py-3 rounded-sm font-bold uppercase tracking-wider text-xs transition-all"
                data-testid="true-blue-shop-all"
              >
                Shop True Blue <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {trueBlueProducts.slice(0, 6).map((product, idx) => (
                <Link
                  key={product.product_id}
                  to={`/products/${product.product_id}`}
                  className="group bg-white/5 backdrop-blur-sm border border-blue-500/20 hover:border-blue-300/60 rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  data-testid={`true-blue-product-${idx}`}
                >
                  <div className="aspect-square bg-white/5 overflow-hidden">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <h3 className="text-white text-xs font-semibold line-clamp-2 min-h-[2rem] group-hover:text-blue-200 transition-colors">{product.name}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-blue-200 text-sm font-bold">${(product.online_price || 0).toFixed(2)}</span>
                      <span className="text-[10px] uppercase tracking-widest text-blue-300/70">True Blue</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="py-20 bg-[#050505]" data-testid="featured-products">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <h2 className="font-heading text-4xl tracking-wide">FEATURED PRODUCTS</h2>
            <Link to="/products" className="text-[#D62828] hover:text-white flex items-center gap-2 uppercase tracking-wide text-sm font-semibold transition-colors">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.product_id} className="product-card rounded-sm overflow-hidden group" data-testid={`product-card-${product.product_id}`}>
                <div className="aspect-square overflow-hidden bg-[#1A1A1A]">
                  <img src={product.image_url || 'https://images.unsplash.com/photo-1692486407015-3a47e4c1181c'} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">{product.category}</p>
                  <h3 className="font-semibold text-white mb-2 line-clamp-2">{product.name}</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[#D62828] font-bold text-lg">${product.online_price.toFixed(2)}</span>
                      {product.msrp && product.msrp > product.online_price && (
                        <span className="text-gray-500 text-xs line-through" data-testid={`product-msrp-${product.product_id}`}>${product.msrp.toFixed(2)}</span>
                      )}
                    </div>
                    <button onClick={() => addToCart(product.product_id)} className="bg-white/10 hover:bg-[#D62828] p-2 rounded-sm transition-colors" data-testid={`add-to-cart-${product.product_id}`}>
                      <ShoppingCart className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIP Banner */}
      <section className="py-20 bg-gradient-to-r from-[#0F0F0F] to-[#1A1A1A] relative overflow-hidden" data-testid="vip-banner">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1765211002533-abda20c22651" alt="VIP Background" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <Crown className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h2 className="font-heading text-4xl sm:text-5xl gold-text mb-6">VIP MONTHLY MEMBERSHIP</h2>
          <p className="text-xl text-gray-300 mb-8">$3.99/month or $40/year - Cancel anytime</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { id: 'vip-discounts', icon: <Percent />, text: 'VIP Discounts' },
              { id: 'vip-gifts', icon: <Gift />, text: 'Monthly Gifts' },
              { id: 'vip-early-access', icon: <Bell />, text: 'Early Access' },
              { id: 'vip-videos', icon: <Play />, text: 'Exclusive Videos' },
            ].map((perk) => (
              <div key={perk.id} className="flex flex-col items-center gap-2 text-gray-300">
                <div className="text-[#D4AF37]">{perk.icon}</div>
                <span className="text-sm">{perk.text}</span>
              </div>
            ))}
          </div>
          <Link to="/vip" className="inline-block bg-[#D4AF37] hover:bg-[#B5952F] text-black px-10 py-4 rounded-sm font-bold uppercase tracking-wider transition-all" data-testid="vip-cta-btn">
            Join VIP Now
          </Link>
        </div>
      </section>

      {/* Polishing University Preview */}
      <section className="py-20 bg-[#050505]" data-testid="university-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl tracking-wide mb-6">POLISHING UNIVERSITY</h2>
              <p className="text-gray-300 text-lg mb-6">Learn from the best. Jimmy Dandridge brings 35+ years of industry experience to help you master the art of metal polishing.</p>
              <ul className="space-y-4 mb-8">
                {[
                  { id: 'uni-fundamentals', label: 'Polishing 101: Fundamentals' },
                  { id: 'uni-advanced', label: 'Beginner to Advanced Techniques' },
                  { id: 'uni-tricks', label: 'Tricks of the Trade' },
                  { id: 'uni-guides', label: 'Equipment & Product Guides' },
                ].map((item) => (
                  <li key={item.id} className="flex items-center gap-3 text-gray-400">
                    <Check className="w-5 h-5 text-[#D62828]" />{item.label}
                  </li>
                ))}
              </ul>
              <Link to="/university" className="inline-flex items-center gap-2 bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider transition-all btn-glow" data-testid="university-cta-btn">
                <Play className="w-5 h-5" />Start Learning
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-video bg-[#1A1A1A] rounded-sm overflow-hidden border border-white/10">
                <img src="https://images.unsplash.com/photo-1738162837672-de9d735a9b90" alt="Polishing Sparks" className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-[#D62828] rounded-full flex items-center justify-center animate-pulse-glow cursor-pointer">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="gallery-section">        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl tracking-wide mb-4">OUR STUNNING GALLERY</h2>
            <p className="text-gray-400 text-lg">See the results. 34+ years of polishing excellence.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { url: 'https://cutabovepolishshop.com/wp-content/uploads/2025/11/rsw_1280h_853.webp', alt: 'Polished semi truck' },
              { url: 'https://cutabovepolishshop.com/wp-content/uploads/2025/11/rsw_1280h_853-1.webp', alt: 'Chrome wheel polish' },
              { url: 'https://cutabovepolishshop.com/wp-content/uploads/2025/11/rsw_2560h_1244.webp', alt: 'Fuel tank polishing' },
              { url: 'https://cutabovepolishshop.com/wp-content/uploads/2025/11/rsw_984h_741.webp', alt: 'Mirror finish metal' },
              { url: 'https://cutabovepolishshop.com/wp-content/uploads/2025/11/rsw_2560h_1244-2.webp', alt: 'Tanker polishing result' },
              { url: 'https://cutabovepolishshop.com/wp-content/uploads/2025/11/rsw_1160h_774.webp', alt: 'Show truck detail' },
              { url: 'https://cutabovepolishshop.com/wp-content/uploads/2025/11/rsw_2560h_1244-1.jpg', alt: 'Fleet polishing' },
              { url: 'https://cutabovepolishshop.com/wp-content/uploads/2025/11/rsw_2560h_1244-3.webp', alt: 'Aluminum wheel shine' },
            ].map((image, idx) => (
              <div key={idx} className="relative aspect-[4/3] overflow-hidden rounded-sm group cursor-pointer" data-testid={`gallery-image-${idx}`}>
                <img src={image.url} alt={image.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white text-sm font-medium">{image.alt}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a href="https://cutabovepolishshop.com/gallery/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#D62828] hover:text-white uppercase tracking-wide text-sm font-semibold transition-colors">
              View Full Gallery <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />
    </div>
  );
};

export default HomePage;
